import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Check, Loader2, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { postApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";

type ConsultantPlanId = "consultant_explorer" | "consultant_pro" | "consultant_elite";

const PLANS: Record<ConsultantPlanId, { price: number; priceId: string }> = {
  consultant_explorer: { price: 399,  priceId: 'price_1TcynCCkUJGKtqQAzAYqUe1n' },
  consultant_pro:      { price: 720,  priceId: 'price_1TcymACkUJGKtqQA13iBWe5k' },
  consultant_elite:    { price: 1440, priceId: 'price_1TcynzCkUJGKtqQAJggREXWd' },
};

const COPY = {
  en: {
    title: "Upgrade your Consultant plan",
    subtitle: "Unlock more visibility and tools by upgrading your subscription.",
    current: "Current plan",
    select: "Choose this plan",
    switch: "Switch to this plan",
    redirecting: "Redirecting to secure checkout…",
    perYear: "/year",
    notAuth: "Please sign in to upgrade.",
    error: "Could not process upgrade. Please try again.",
    plans: {
      consultant_explorer: { name: "Explorer", desc: "Public profile, up to 3 expertise areas." },
      consultant_pro:      { name: "Pro",      desc: "Unlimited expertise areas, deal alerts, certification badges." },
      consultant_elite:    { name: "Elite",    desc: "Premium visibility, reduced commission, dedicated account manager." },
    },
    features: {
      consultant_explorer: ["Public consultant profile", "Up to 3 expertise areas", "Basic client messaging", "E-Learning access"],
      consultant_pro:      ["Everything in Explorer", "Unlimited expertise areas", "Client messaging & deal alerts", "Certification badges", "Priority visibility"],
      consultant_elite:    ["Everything in Pro", "Premium visibility", "Reduced commission rate", "Priority support", "Dedicated account manager"],
    },
  },
  fr: {
    title: "Améliorez votre forfait Consultant",
    subtitle: "Débloquez plus de visibilité et d'outils en améliorant votre abonnement.",
    current: "Forfait actuel",
    select: "Choisir ce forfait",
    switch: "Basculer vers ce forfait",
    redirecting: "Redirection vers le paiement sécurisé…",
    perYear: "/an",
    notAuth: "Veuillez vous connecter pour améliorer votre forfait.",
    error: "Impossible de traiter la mise à niveau. Réessayez.",
    plans: {
      consultant_explorer: { name: "Explorer", desc: "Profil public, jusqu'à 3 domaines d'expertise." },
      consultant_pro:      { name: "Pro",      desc: "Domaines illimités, alertes deals, badges de certification." },
      consultant_elite:    { name: "Elite",    desc: "Visibilité premium, commission réduite, gestionnaire dédié." },
    },
    features: {
      consultant_explorer: ["Profil consultant public", "Jusqu'à 3 domaines d'expertise", "Messagerie client basique", "Accès E-Learning"],
      consultant_pro:      ["Tout d'Explorer", "Domaines d'expertise illimités", "Messagerie client & alertes deals", "Badges de certification", "Visibilité prioritaire"],
      consultant_elite:    ["Tout de Pro", "Visibilité premium", "Taux de commission réduit", "Support prioritaire", "Gestionnaire de compte dédié"],
    },
  },
} as const;

type UpgradePreview = {
  amountDueToday: number;
  creditFromCurrentPlan: number;
  chargeForNewPlan: number;
  nextBillingAmount: number;
  nextBillingDate: string | null;
  currency: string;
  currentCard: { brand: string; last4: string } | null;
};

export default function ConsultantUpgrade() {
  const { user, loading } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { toast } = useToast();
  const [busy, setBusy] = useState<ConsultantPlanId | null>(null);
  const [currentTier, setCurrentTier] = useState<ConsultantPlanId | null>(null);

  const [upgradeDialog, setUpgradeDialog] = useState<{
    open: boolean;
    planId: ConsultantPlanId | null;
    loading: boolean;
    preview: UpgradePreview | null;
    error: string | null;
  }>({ open: false, planId: null, loading: false, preview: null, error: null });

  const t = COPY[(language as "en" | "fr") || "en"];
  const presetPlan = (params.get("plan") || "").trim() as ConsultantPlanId | "";

  useEffect(() => {
    if (loading) return;
    if (!user) {
      const next = encodeURIComponent(`/consultant/upgrade${presetPlan ? `?plan=${presetPlan}` : ""}`);
      navigate(`/login?next=${next}`, { replace: true });
    }
  }, [loading, user, navigate, presetPlan]);

  // Charge le tier actuel depuis Firestore (consultantTier)
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { doc, getDoc } = await import("firebase/firestore");
        const { db } = await import("@/lib/firebase");
        const snap = await getDoc(doc(db, "users", user.uid));
        const tier = snap.exists() ? snap.data()?.consultantTier : null;
        if (tier) setCurrentTier(tier as ConsultantPlanId);
      } catch (err) {
        console.warn("[ConsultantUpgrade] could not load current tier", err);
      }
    })();
  }, [user]);

  const hasActiveSub = useMemo(() => !!currentTier, [currentTier]);

  const handleCheckout = async (planId: ConsultantPlanId) => {
    if (!user) {
      toast({ title: t.notAuth, variant: "destructive" });
      return;
    }
    setBusy(planId);
    try {
      const { getAuth } = await import("firebase/auth");
      const currentUser = getAuth().currentUser;
      const idToken = await currentUser?.getIdToken(true);
      const res = await fetch(`${import.meta.env.VITE_API_URL}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          type: "subscription",
          planId,
          amount: 0,
          currency: "usd",
          successUrl: `${window.location.origin}/payment-success?role=consultant`,
          cancelUrl: `${window.location.origin}/consultant/upgrade`,
        }),
      });
      const data = await res.json();
      if (!data?.url) throw new Error("Missing checkout URL");
      window.location.href = data.url;
    } catch (err) {
      toast({ title: t.error, description: String((err as Error)?.message || ""), variant: "destructive" });
      setBusy(null);
    }
  };

  const handleSelect = async (planId: ConsultantPlanId) => {
    if (hasActiveSub) {
      if (planId === currentTier) return;

      setUpgradeDialog({ open: true, planId, loading: true, preview: null, error: null });
      try {
        const { getAuth } = await import("firebase/auth");
        const currentUser = getAuth().currentUser;
        if (currentUser) await currentUser.getIdToken(true);
        const data = await postApi("/payments/preview-subscription-change", { newTier: planId });
        setUpgradeDialog((prev) => ({ ...prev, loading: false, preview: data }));
      } catch (err: any) {
        setUpgradeDialog((prev) => ({ ...prev, loading: false, error: err?.message || "Preview failed" }));
      }
    } else {
      handleCheckout(planId);
    }
  };

  const handleConfirmUpgrade = async () => {
    if (!upgradeDialog.planId) return;
    setBusy(upgradeDialog.planId);
    try {
      const { getAuth } = await import("firebase/auth");
      const currentUser = getAuth().currentUser;
      if (currentUser) await currentUser.getIdToken(true);
      await postApi("/payments/change-subscription", { newTier: upgradeDialog.planId });
      setCurrentTier(upgradeDialog.planId);
      setUpgradeDialog({ open: false, planId: null, loading: false, preview: null, error: null });
      toast({
        title: language === "fr" ? "Forfait mis à jour !" : "Plan upgraded!",
        description: language === "fr"
          ? "Votre abonnement a été mis à jour avec prorata."
          : "Your subscription has been updated with proration.",
      });
      navigate("/dashboard/consultant");
    } catch (err: any) {
      toast({
        title: language === "fr" ? "Erreur" : "Error",
        description: err?.message || "Unknown error",
        variant: "destructive",
      });
    } finally {
      setBusy(null);
    }
  };

  const formatAmount = (cents: number, currency: string) =>
    new Intl.NumberFormat("en-US", {
      style: "currency", currency: currency.toUpperCase(), minimumFractionDigits: 2,
    }).format(Math.abs(cents) / 100);

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <Sparkles className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h1 className="text-3xl font-bold">{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {(Object.keys(PLANS) as ConsultantPlanId[]).map((id) => {
            const plan = PLANS[id];
            const isCurrent = id === currentTier;
            const showSwitchLabel = hasActiveSub && !isCurrent;

            return (
              <Card key={id} className={isCurrent ? "border-primary" : ""}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{t.plans[id].name}</CardTitle>
                    {isCurrent && <Badge variant="secondary">{t.current}</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground">{t.plans[id].desc}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <span className="text-3xl font-bold">${plan.price.toLocaleString()}</span>
                    <span className="text-muted-foreground ml-1 text-sm">{t.perYear}</span>
                  </div>
                  <ul className="space-y-2 text-sm">
                    {t.features[id].map((f) => (
                      <li key={f} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className="w-full"
                    disabled={isCurrent || busy !== null}
                    onClick={() => handleSelect(id)}
                  >
                    {busy === id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t.redirecting}
                      </>
                    ) : showSwitchLabel ? t.switch : t.select}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      <Dialog open={upgradeDialog.open} onOpenChange={(open) => !open && setUpgradeDialog((prev) => ({ ...prev, open: false }))}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{language === "fr" ? "Confirmer la mise à niveau" : "Confirm Upgrade"}</DialogTitle>
            <DialogDescription>{upgradeDialog.planId ? t.plans[upgradeDialog.planId].name : ""}</DialogDescription>
          </DialogHeader>

          {upgradeDialog.loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              {language === "fr" ? "Calcul du prorata..." : "Calculating proration..."}
            </div>
          )}

          {upgradeDialog.error && (
            <div className="flex items-start gap-2 text-sm text-destructive py-4">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{upgradeDialog.error}</span>
            </div>
          )}

          {upgradeDialog.preview && !upgradeDialog.loading && (
            <div className="space-y-4 py-2">
              <div className="rounded-md border p-4 space-y-3">
                <div className="text-sm font-medium">
                  {language === "fr" ? "Détail du prorata" : "Proration Details"}
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {language === "fr" ? "Crédit plan actuel" : "Credit from current plan"}
                    </span>
                    <span className="font-medium text-green-600">
                      -{formatAmount(Math.abs(upgradeDialog.preview.creditFromCurrentPlan), upgradeDialog.preview.currency)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      {language === "fr" ? "Charge nouveau plan" : "Charge for new plan"}
                    </span>
                    <span className="font-medium">
                      +{formatAmount(upgradeDialog.preview.chargeForNewPlan, upgradeDialog.preview.currency)}
                    </span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-base">
                    <span className="font-semibold">{language === "fr" ? "Dû aujourd'hui" : "Due today"}</span>
                    <span className="font-bold">
                      {formatAmount(upgradeDialog.preview.amountDueToday, upgradeDialog.preview.currency)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setUpgradeDialog((prev) => ({ ...prev, open: false }))} disabled={busy !== null}>
              {language === "fr" ? "Annuler" : "Cancel"}
            </Button>
            <Button
              onClick={handleConfirmUpgrade}
              disabled={!upgradeDialog.preview || busy !== null || upgradeDialog.loading}
              className="bg-[#1D314D] hover:bg-[#2E4663] text-white"
            >
              {busy !== null && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {language === "fr" ? "Confirmer la mise à niveau" : "Confirm Upgrade"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}